import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Headphones, Mic, Download, ExternalLink } from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { podcastEpisodes } from '../data/podcasts'

/**
 * Podcast appearances, on a page Camilla can share with hosts and bookers.
 * The episode list is shared with /media via data/podcasts.ts.
 */

const DESCRIPTION =
  'Camilla Thompson on 30+ health and wellness podcasts, covering biohacking, longevity, energy, sleep and biological age. Listen to every episode, and enquire about having her on your show.'

const TOPICS = [
  'Biohacking without the extremes',
  'Longevity and healthspan',
  'Reversing biological age',
  'Energy and mitochondrial health',
  'Sleep and circadian rhythm',
  'Mould illness and recovery',
  'Women’s health and perimenopause',
  'Gut health and the microbiome',
  'Executive performance and burnout',
  'DNA methylation and epigenetics',
]

export default function PodcastsPage() {
  const total = podcastEpisodes.length

  return (
    <>
      <Helmet>
        <title>Podcast Appearances | Camilla Thompson | BioHackMe</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://www.biohackme.com.au/podcasts" />
        <meta property="og:title" content="Podcast Appearances | Camilla Thompson" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.biohackme.com.au/podcasts" />
        <meta property="og:image" content="https://www.biohackme.com.au/images/camilla-speaking-bw.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.biohackme.com.au/images/camilla-speaking-bw.webp" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Camilla Thompson podcast appearances',
            numberOfItems: total,
            itemListElement: podcastEpisodes.map((p, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: p.name,
              url: p.link,
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.biohackme.com.au' },
              { '@type': 'ListItem', position: 2, name: 'Media', item: 'https://www.biohackme.com.au/media' },
              { '@type': 'ListItem', position: 3, name: 'Podcasts', item: 'https://www.biohackme.com.au/podcasts' },
            ],
          })}
        </script>
      </Helmet>

      <Header />

      <main className="bg-white">
        {/* Hero */}
        <section className="bg-ocean text-white pt-28 md:pt-36 pb-14 md:pb-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-gold mb-4">
                International Keynote Speaker | Author | Coach
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-brand font-bold uppercase tracking-tight mb-5">
                Podcast appearances
              </h1>
              <p className="text-[15px] md:text-base text-white/75 leading-relaxed max-w-2xl mb-8">
                I've been a guest on {total}+ health, business and wellness podcasts, talking about
                biohacking that people can actually use. No extremes, no $10,000 protocols, just the
                small changes that move the needle.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="btn btn--primary-on-dark btn--sm">
                  <Mic className="w-4 h-4" />
                  Have me on your show
                </Link>
                <a
                  href="/BioHackMe-Media-Kit.pdf"
                  download
                  className="btn btn--sm border border-white/40 text-white hover:bg-white hover:text-ocean transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download media kit
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Episodes */}
        <section className="py-14 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-ocean mb-2">Every episode</h2>
              <p className="text-[15px] md:text-base text-charcoal/70 mb-8">
                Newest first. Each one opens in a new tab.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {podcastEpisodes.map((podcast) => (
                  <li key={podcast.id}>
                    <a
                      href={podcast.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 bg-ice hover:bg-ocean hover:text-white text-ocean px-4 py-3 rounded-xl text-sm font-semibold transition-colors duration-200 h-full"
                    >
                      <Headphones className="w-4 h-4 flex-shrink-0 text-gold-dark" />
                      <span className="truncate">{podcast.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* What I talk about */}
        <section className="py-14 md:py-20 bg-cloud">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-ocean mb-2">What I talk about</h2>
              <p className="text-[15px] md:text-base text-charcoal/70 mb-8 max-w-2xl">
                Pick any of these and I'll bring the research, the practical version and the stories.
                I'm happy to tailor a topic to your audience.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                {TOPICS.map((topic) => (
                  <li key={topic} className="flex items-start text-[15px] md:text-base text-charcoal/80">
                    <span className="text-gold-dark mr-3 flex-shrink-0">•</span>
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Why book */}
        <section className="py-14 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-5">
              <div className="bg-ice rounded-2xl p-6 border border-ocean/15">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark mb-3">
                  The credentials
                </p>
                <p className="text-[15px] md:text-base text-charcoal/80 leading-relaxed">
                  Nationally Recognised Nutritionist, ICF PCC Executive Coach, and author of{' '}
                  <strong>Biohack Me</strong>, Australia's first book on biohacking and an Australian
                  Business Book Awards finalist.
                </p>
              </div>
              <div className="bg-ice rounded-2xl p-6 border border-ocean/15">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark mb-3">
                  The proof
                </p>
                <p className="text-[15px] md:text-base text-charcoal/80 leading-relaxed">
                  I reversed my own biological age by 16 years, verified by GlycanAge testing at 49,
                  after recovering from mould illness. I talk about what worked and what didn't.
                </p>
              </div>
              <div className="bg-ice rounded-2xl p-6 border border-ocean/15">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark mb-3">
                  The angle
                </p>
                <p className="text-[15px] md:text-base text-charcoal/80 leading-relaxed">
                  Biohacking that is accessible, affordable and achievable. Built on 8 foundational
                  pillars, for people with jobs and families rather than unlimited time and budget.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-ocean text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Want me on your podcast?</h2>
              <p className="text-[15px] md:text-base text-white/75 mb-8 max-w-xl mx-auto">
                Tell me about your show and your audience, and I'll come prepared. Remote or in
                person, Australia or anywhere.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/contact" className="btn btn--primary-on-dark btn--sm">
                  Get in touch
                </Link>
                <Link
                  to="/media"
                  className="btn btn--sm border border-white/40 text-white hover:bg-white hover:text-ocean transition-colors"
                >
                  See all media
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
